import invariant from 'invariant';
import { useLocation } from 'react-router-dom';

import getSearchUrl from 'algolia-api/search/getSearchUrl';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { MAIN_SEARCH_FACETS } from 'utils/mainSearchFacets';

type Args = {
  currentObjectId: string;
};

export function useSearchContextHeaderInput({ currentObjectId }: Args) {
  const location = useLocation<{
    originatingPathname?: string;
    showSearchContext?: boolean;
  }>();

  const {
    filters,
    pageIndex,
    query,
    resultsByPage,
    sort,
    isSearching,
    searchLocation,
  } = useMainSelector((state) => state.mainSearch);
  const searchRadius = useMainSelector((state) => state.searchRadius);

  if (!location.state?.showSearchContext) {
    return { searchContext: undefined, isSearching };
  }

  const results = resultsByPage[pageIndex];
  if (!results) {
    return { searchContext: undefined, isSearching };
  }

  const currentResultIndex = results.hits.findIndex(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (hit: any) => hit.objectID === currentObjectId,
  );

  if (currentResultIndex === -1) return {};

  let previousResult = null;
  let previousResultPageIndex = null;

  if (currentResultIndex > 0) {
    previousResult = resultsByPage[pageIndex].hits[currentResultIndex - 1];
    previousResultPageIndex = pageIndex;
  } else if (pageIndex > 0) {
    const previousPage = resultsByPage[pageIndex - 1];

    if (previousPage) {
      previousResult = previousPage.hits[(previousPage.hitsPerPage || 0) - 1];

      if (previousResult) {
        previousResultPageIndex = pageIndex - 1;
      }
    }
  }

  let nextResult = null;
  let nextResultPageIndex = null;

  if (currentResultIndex < (results.hitsPerPage || 0) - 1) {
    nextResult = results.hits[currentResultIndex + 1];
    nextResultPageIndex = results.page;
  } else if (pageIndex < results.nbPages - 1) {
    const nextPage = resultsByPage[pageIndex + 1];

    if (nextPage) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      nextResult = nextPage.hits[0];
      nextResultPageIndex = pageIndex + 1;
    }
  }

  const originatingPathname = location.state?.originatingPathname;
  invariant(filters.type, 'Filter type required for main search url');

  const searchResultsPath = getSearchUrl({
    filters,
    searchFacets: MAIN_SEARCH_FACETS,
    query,
    pageIndex,
    // @ts-expect-error fix types
    sort,
    originatingPathname,
    // @ts-expect-error fix types
    location: searchLocation,
    radius: searchRadius.value,
    scrollToId: currentObjectId,
    currentSearchParams: location.search,
  });

  return {
    searchContextInput: {
      currentResultIndex,
      pageIndex,
      resultsByPage,
      searchResultsPath,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
      originatingPathname,
    },
    isSearching,
  };
}

export type SearchContextInput = NonNullable<
  ReturnType<typeof useSearchContextHeaderInput>['searchContextInput']
>;
